import { useState, useEffect, useId } from 'react';
import { parseCurlPreview, curlToEndpoint } from '../../utils/curlParser';
import { parseEndpointImport, toCreateRequest } from '../../utils/exportEndpoint';
import type { EndpointExport, EndpointImportItem } from '../../utils/exportEndpoint';
import { Modal } from '../common/Modal';
import { TabList, tabPanelProps } from '../common/Tabs';
import { Alert } from '../common/Alert';
import { StatusBadge } from '../common/StatusBadge';
import { LoadingLabel } from '../common/Cursor';

type ImportMode = 'curl' | 'json';

const MODE_TABS: { key: ImportMode; label: string }[] = [
  { key: 'curl', label: 'cURL' },
  { key: 'json', label: 'JSON' },
];

interface ImportEndpointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (items: EndpointImportItem[]) => Promise<void>;
  isLoading?: boolean;
}

interface CurlPreview {
  method: string;
  path: string;
  headers: Record<string, string>;
  body: string | null;
  url: string;
}

export function ImportEndpointModal({ isOpen, onClose, onImport, isLoading }: ImportEndpointModalProps) {
  const [mode, setMode] = useState<ImportMode>('curl');
  const tabsId = useId();
  const [curlInput, setCurlInput] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [curlPreview, setCurlPreview] = useState<CurlPreview | null>(null);
  const [jsonPreview, setJsonPreview] = useState<EndpointExport[] | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCurlInput('');
      setJsonInput('');
      setError(null);
      setCurlPreview(null);
      setJsonPreview(null);
      setMode('curl');
    }
  }, [isOpen]);

  const handleCurlParse = () => {
    setError(null);
    setCurlPreview(null);
    try {
      const preview = parseCurlPreview(curlInput);
      setCurlPreview(preview);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse cURL command');
    }
  };

  const handleJsonParse = () => {
    setError(null);
    setJsonPreview(null);
    try {
      setJsonPreview(parseEndpointImport(JSON.parse(jsonInput)));
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON. Please check your input.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to parse JSON');
      }
    }
  };

  const handleImport = async () => {
    setError(null);
    try {
      if (mode === 'curl') {
        await onImport([{ endpoint: curlToEndpoint(curlInput) }]);
      } else if (mode === 'json' && jsonPreview) {
        await onImport(jsonPreview.map(ep => ({ endpoint: toCreateRequest(ep), rules: ep.rules })));
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    }
  };

  const canImport = (mode === 'curl' && curlPreview !== null) || (mode === 'json' && jsonPreview !== null && jsonPreview.length > 0);

  const headerCount = curlPreview ? Object.keys(curlPreview.headers).length : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Endpoints"
      titleClassName="font-bold text-lg mb-4"
      boxClassName="max-w-2xl"
    >
      {/* Mode tabs */}
      <TabList
        tabs={MODE_TABS}
        active={mode}
        onChange={(key) => { setMode(key); setError(null); }}
        idPrefix={tabsId}
        className="tabs tabs-border mb-4"
      />

      {error && (
        <Alert variant="error" className="mb-4">{error}</Alert>
      )}

      {/* cURL mode */}
      {mode === 'curl' && (
        <div {...tabPanelProps(tabsId, 'curl')}>
          <label htmlFor={`${tabsId}-curl-input`} className="fieldset-legend text-sm">Paste a cURL command</label>
          <textarea
            id={`${tabsId}-curl-input`}
            value={curlInput}
            onChange={(e) => { setCurlInput(e.target.value); setCurlPreview(null); setError(null); }}
            placeholder={`curl -X POST 'https://api.example.com/users' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"name": "John"}'`}
            rows={5}
            className="textarea w-full font-mono text-xs"
            spellCheck={false}
            disabled={isLoading}
          />
          {!curlPreview && curlInput.trim() && (
            <button
              onClick={handleCurlParse}
              className="btn btn-sm btn-outline mt-2"
              disabled={isLoading}
            >
              Parse
            </button>
          )}

          {/* cURL preview */}
          {curlPreview && (
            <div className="mt-4 border border-base-300 rounded-box p-4 bg-base-200">
              <h3 className="text-sm font-semibold mb-2">Preview</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-base-content/80">Method:</span>{' '}
                  <span className="font-mono font-semibold">{curlPreview.method}</span>
                </div>
                <div>
                  <span className="text-base-content/80">Path:</span>{' '}
                  <span className="font-mono">{curlPreview.path}</span>
                </div>
              </div>
              {headerCount > 0 && (
                <div className="mt-2 text-xs text-base-content/70">
                  {headerCount} header{headerCount !== 1 ? 's' : ''} detected (headers are for reference only)
                </div>
              )}
              {curlPreview.body && (
                <div className="mt-2">
                  <span className="text-xs text-base-content/80">Body:</span>
                  <pre className="bg-base-300 p-2 rounded-box text-xs font-mono mt-1 overflow-x-auto max-h-32 overflow-y-auto">
                    {(() => {
                      try { return JSON.stringify(JSON.parse(curlPreview.body!), null, 2); }
                      catch { return curlPreview.body; }
                    })()}
                  </pre>
                </div>
              )}
              <p className="text-xs text-base-content/70 mt-3">
                This will create an endpoint at <code className="bg-base-300 px-1 rounded-field">{curlPreview.path}</code> with a mock response body.
              </p>
            </div>
          )}
        </div>
      )}

      {/* JSON mode */}
      {mode === 'json' && (
        <div {...tabPanelProps(tabsId, 'json')}>
          <label htmlFor={`${tabsId}-json-input`} className="fieldset-legend text-sm">Paste endpoint JSON (mockd export format or array of endpoints)</label>
          <textarea
            id={`${tabsId}-json-input`}
            value={jsonInput}
            onChange={(e) => { setJsonInput(e.target.value); setJsonPreview(null); setError(null); }}
            placeholder={`{\n  "endpoints": [\n    {\n      "path": "/api/users",\n      "statusCode": 200,\n      "responseBody": "{\\"users\\": []}"\n    }\n  ]\n}`}
            rows={8}
            className="textarea w-full font-mono text-xs"
            spellCheck={false}
            disabled={isLoading}
          />
          {!jsonPreview && jsonInput.trim() && (
            <button
              onClick={handleJsonParse}
              className="btn btn-sm btn-outline mt-2"
              disabled={isLoading}
            >
              Parse
            </button>
          )}

          {/* JSON preview */}
          {jsonPreview && (
            <div className="mt-4 border border-base-300 rounded-box p-4 bg-base-200">
              <h3 className="text-sm font-semibold mb-2">
                {jsonPreview.length} endpoint{jsonPreview.length !== 1 ? 's' : ''} found
              </h3>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {jsonPreview.map((ep, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <StatusBadge status={ep.statusCode ?? 200} />
                    <span className="font-mono text-xs">{ep.path}</span>
                    {ep.rules && ep.rules.length > 0 && (
                      <span className="text-xs text-base-content/70">
                        · {ep.rules.length} rule{ep.rules.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {ep.responseHeaders && Object.keys(ep.responseHeaders).length > 0 && (
                      <span className="text-xs text-base-content/70">· headers</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="modal-action">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isLoading}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleImport}
          disabled={!canImport || isLoading}
        >
          {isLoading ? (
            <LoadingLabel>Importing</LoadingLabel>
          ) : (
            <>
              Import{mode === 'json' && jsonPreview ? ` ${jsonPreview.length} endpoint${jsonPreview.length !== 1 ? 's' : ''}` : ''}
            </>
          )}
        </button>
      </div>
    </Modal>
  );
}
